import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Video } from "@/types/video";

export interface LearningRecord {
  id: string;
  videoId: string;
  progress: number;
  lastPosition: number;
  watchDuration: number;
  completed: boolean;
  updatedAt: string;
  video: Video;
}

export function useHistory() {
  const { user } = useAuth();
  const [history, setHistory] = useState<LearningRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setHistory([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchHistory = async () => {
      try {
        setError(null);
        const { data, error } = await supabase
          .from("learning_records")
          .select(`
            id,
            video_id,
            progress,
            last_position,
            watch_duration,
            completed,
            updated_at,
            videos (
              id,
              title,
              description,
              thumbnail_url,
              video_url,
              duration,
              view_count,
              like_count,
              category,
              jlpt_level,
              created_at,
              updated_at,
              created_by,
              is_published,
              is_premium
            )
          `)
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false });

        if (error) throw error;

        // Transform the data and filter out records where video is null (deleted videos)
        const records: LearningRecord[] = data
          .filter((item: any) => item.videos) // Filter out null videos
          .map((item: any) => ({
            id: item.id,
            videoId: item.video_id,
            progress: item.progress,
            lastPosition: item.last_position,
            watchDuration: item.watch_duration,
            completed: item.completed,
            updatedAt: item.updated_at,
            video: {
              id: item.videos.id,
              title: item.videos.title,
              description: item.videos.description,
              thumbnailUrl: item.videos.thumbnail_url,
              videoUrl: item.videos.video_url,
              duration: item.videos.duration,
              viewCount: item.videos.view_count,
              likeCount: item.videos.like_count,
              category: item.videos.category,
              jlptLevel: item.videos.jlpt_level,
              createdAt: item.videos.created_at,
              updatedAt: item.videos.updated_at,
              createdBy: item.videos.created_by,
              isPublished: item.videos.is_published,
              isPremium: item.videos.is_premium
            }
          }));

        setHistory(records);
      } catch (error) {
        console.error("Error fetching history:", error);
        setError((error as any)?.message || "获取学习记录失败");
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [user]);

  return { history, isLoading, error };
}
